import * as d3 from "d3";
import {SidebarTab} from "../../Sidebar";
import {ITypeRadar} from "../../../types";
import {xScale} from "../../../mocks/projectsMock";
import {useEffect} from "react";

export const wrapText = (
  text: string,
  maxWidth: number,
  x: number,
  y: number,
  mainGroup: any
): string[] => {
  const words: string[] = text.split(" ");
  let line: string = "";
  const lines: string[] = [];

  words.forEach((word: string, index: number) => {
    const testLine: string = line + word + " ";
    const testText = mainGroup
      .append("text")
      .attr("x", x)
      .attr("y", y)
      .text(testLine)
      .style("visibility", "hidden")
      .style("font-size", `13px`)
      .style("font-weight", "400")
      .style("pointer-events", "none")
      .style("text-anchor", "start");

    const node = testText.node();
    const testWidth: number = node ? node.getBBox().width : 0;
    testText.remove();

    if (testWidth > maxWidth && line) {
      if (line.trim().endsWith(" и")) {
        lines.push(line.trim().slice(0, -2));
        line = "и " + word + " ";
      } else {
        lines.push(line.trim());
        line = word + " ";
      }
    } else {
      line = testLine;
    }

    if (word === "и" && index < words.length - 1) {
      const nextWord = words[index + 1];
      const testLineWithNextWord = line + nextWord + " ";
      const testTextWithNextWord = mainGroup
        .append("text")
        .attr("x", x)
        .attr("y", y)
        .text(testLineWithNextWord)
        .style("visibility", "hidden")
        .style("font-size", `13px`)
        .style("font-weight", "400")
        .style("pointer-events", "none")
        .style("text-anchor", "start");

      const testWidthWithNextWord: number = testTextWithNextWord.node()
        ? testTextWithNextWord.node().getBBox().width
        : 0;
      testTextWithNextWord.remove();

      if (testWidthWithNextWord <= maxWidth) {
        line = testLineWithNextWord;
        words[index + 1] = "";
      }
    }
  });

  lines.push(line.trim());
  return lines;
};

export interface Icoord {
  id: number | string;
  x: number;
  y: number;
}

interface SegmentLabelProps {
  mainGroup: d3.Selection<SVGGElement, unknown, null, undefined>;
  angle: number;
  label: string;
  radius: number;
  segmentColor: {back: string; color: string};
  isActiveDirection: boolean;
  openSidebarWithTab: (
    tab: any,
    directionId: number,
    clussterId?: number,
    name?: any,
    sectionName?: any,
    colorTitle?: {back: string; color: string},
    nameTehnology?: string,
    idTehnology?: string,
    typeRadar?: ITypeRadar
  ) => void;
  directions: {
    id: number | string;
    name: string;
    nptl: {
      createdAt: string;
      documentId: string;
      id: number;
      name: string;
      publishedAt: string;
      updatedAt: string;
    };
  }[];
  segmentIndex: number;
  activeDirectionId: number | string | null | undefined;
  typeRadar: string;
  isWeak_signal?: boolean;
  isNationalProjects?: boolean;
  backColor?: string;
  isNPTL?: boolean;
  isBtn?: boolean;
  setCoordinatesDirectionActive?: (position: Icoord[]) => void;
  coordinatesDirectionActive?: Icoord[];
}

const renderSegmentLabel = ({
  mainGroup,
  angle,
  label,
  radius,
  segmentColor,
  isActiveDirection,
  openSidebarWithTab,
  directions,
  segmentIndex,
  activeDirectionId,
  typeRadar,
  isWeak_signal,
  isNationalProjects,
  backColor,
  isNPTL,
  isBtn,
  setCoordinatesDirectionActive,
  coordinatesDirectionActive,
}: SegmentLabelProps) => {
  // console.log("directions: ", directions);
  if (label) {
    // if (!isWeak_signal) {
    const textRadius = radius + 50;
    const textX = Math.cos((angle * Math.PI) / 180) * textRadius;
    const textY = Math.sin((angle * Math.PI) / 180) * textRadius;
    const fontSize = 11 * xScale;
    const maxTextWidth = 150 * xScale;
    const lines: string[] = wrapText(
      label,
      maxTextWidth,
      textX,
      textY,
      mainGroup
    );

    let maxLineWidth: number = 0;
    lines.forEach((line: string) => {
      const testText = mainGroup
        .append("text")
        .attr("x", textX)
        .attr("y", textY)
        .text(line)
        .style("visibility", "hidden")
        .style("font-size", `${fontSize}px`)
        .style("font-weight", "400")
        .style("text-anchor", "start");

      const node = testText.node();
      const testWidth: number = node ? node.getBBox().width : 0;
      testText.remove();

      if (testWidth > maxLineWidth) {
        maxLineWidth = testWidth;
      }
    });

    // const tempText = mainGroup
    //   .append("text")
    //   .attr("x", textX)
    //   .attr("y", textY)
    //   .text(label)
    //   .style("font-size", "20px")
    //   .style("font-weight", "700")
    //   .style("visibility", "hidden");

    // //@ts-ignore
    // const bbox = tempText.node().getBBox();
    // tempText.remove();

    const offset = 1 * xScale;
    const paddingX = 6;
    const paddingY = 3;
    const lineHeight = fontSize + 1;
    const textHeight = lines.length * lineHeight;
    const backgroundWidth = maxLineWidth + paddingX * 2;
    const backgroundHeight = textHeight + paddingY * 4;
    const extraOffset = 1 * xScale;

    const backgroundRectX =
      textX > 0 ? textX + offset + 4 : textX - backgroundWidth - 5;
    const backgroundRectY = textY - offset - backgroundHeight - extraOffset;

    const isRightSide = angle > -90 && angle < 95;
    const rectX = isRightSide
      ? textX - paddingX
      : textX - backgroundWidth - paddingX * 2;
    const rectY = textY - backgroundHeight / 2;
    const backgroundRect = mainGroup
      .append("rect")
      .attr("x", rectX)
      .attr("y", rectY)
      .attr("width", backgroundWidth + paddingX * 2)
      .attr("height", backgroundHeight)
      .attr("stroke", isNationalProjects ? backColor ?? "none" : "none")
      .attr(
        "fill",
        isNationalProjects
          ? isNPTL
            ? backColor ?? "rgba(0, 0, 0, 0.1)"
            : "rgba(0, 0, 0, 0.1)"
          : segmentColor.back
      )
      .attr(
        "opacity",
        isNationalProjects &&
          activeDirectionId &&
          activeDirectionId !== directions[segmentIndex]?.id
          ? 0.2
          : isNationalProjects &&
            activeDirectionId &&
            activeDirectionId === directions[segmentIndex]?.id
          ? 0.8
          : isActiveDirection
          ? 1
          : 0.6
      )
      .attr("rx", 12)
      .attr("ry", 12);

    lines.forEach((line: string, index: number) => {
      mainGroup
        .append("text")
        .attr("x", rectX + backgroundWidth / 2 + 4)
        .attr("y", rectY + paddingY + (index + 0.5) * lineHeight + 2)
        .text(line)
        .style("fill", isNationalProjects ? "#ffffff" : segmentColor.color)
        .style("font-size", `${fontSize}px`)
        .style("font-weight", "600")
        .style("text-anchor", "middle")
        .style("pointer-events", "none")
        .attr("dy", "0.35em")
        .attr(
          "opacity",
          isNationalProjects &&
            activeDirectionId &&
            activeDirectionId !== directions[segmentIndex]?.id
            ? 0.5
            : 1
        )
        .on("mouseenter", function () {
          d3.select(this).attr("opacity", 1);
        })
        .on("mouseleave", function () {
          if (!isActiveDirection) {
            d3.select(this).attr(
              "opacity",
              isNationalProjects && activeDirectionId ? 0.5 : 1
            );
          }
        });
    });

    if (typeRadar === "map" || typeRadar === "molecula") {
      backgroundRect
        .style("cursor", isBtn ? "pointer" : "default")
        .on("mouseenter", function () {
          backgroundRect.attr("opacity", 0.8);
        })
        .on("mouseleave", function () {
          if (!isActiveDirection) {
            backgroundRect.attr(
              "opacity",
              isNationalProjects && activeDirectionId ? 0.2 : 0.6
            );
          }
        })
        .on("click", () => {
          if (!isBtn) return;
          openSidebarWithTab(
            SidebarTab.Clusters,
            directions[segmentIndex].id as number,
            undefined,
            undefined,
            directions[segmentIndex].name,
            segmentColor,
            undefined,
            undefined,
            typeRadar
          );
        });
    }

    if (setCoordinatesDirectionActive && coordinatesDirectionActive) {
      const newPosition: Icoord = {
        id: directions[segmentIndex].id,
        x: rectX + backgroundWidth / 2,
        y: rectY,
      };
      if (
        !coordinatesDirectionActive.some((coord) => coord.id === newPosition.id)
      ) {
        coordinatesDirectionActive.push(newPosition);
      }
    }
  }

  return null;
};

export default renderSegmentLabel;
